/**
 * «Suscripción vencida»: la única pantalla de una cuenta suspendida.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ UNA PANTALLA ENTERA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Pasados los días de gracia, el servidor ya no deja vender ni cambiar nada
 * (`app/core/suscripcion.py`). Pero la app seguía enseñándolo todo, y cada
 * botón fallaba por su lado con un error suelto: parecía que Softfi estaba
 * roto, no que había que pagar. El dueño lo pidió así: «después del día de
 * gracia, la pantalla completa cada vez que alguien ingrese», solo para pagar
 * o contactar a Softfi. Formal, «pero tampoco tan resaltante».
 *
 * Quien puede pagar (el dueño) ve el contacto del asesor y el formulario para
 * reportar el pago. Un cajero no puede hacer nada: se le dice con quién hablar.
 * Y cualquiera puede ir a otro de sus negocios o cerrar la sesión: una cuenta
 * suspendida no puede dejar a nadie encerrado.
 */

import { useState } from "react";
import { CalendarX2, LogOut, MessageCircle } from "lucide-react";
import { LogoConNombre } from "@/components/Logo";
import { Nota, Tarjeta } from "@/components/ui";
import { AvisarPago } from "@/features/ajustes/AvisarPago";
import { mensajeDeError } from "@/lib/api";
import type { EstadoDeSuscripcion } from "@/lib/types";
import { useAuth } from "@/stores/auth";
import { aviso } from "@/stores/ui";

/** «24 de septiembre de 2026». Al mediodía, para que ningún huso lo corra un día. */
function fechaLarga(dia: string): string {
  return new Date(`${dia}T12:00:00`).toLocaleDateString("es-VE", { dateStyle: "long" });
}

export function CuentaSuspendida({ estado }: { estado: EstadoDeSuscripcion }) {
  const { tenant, tenants, cambiarNegocio, cerrarSesion } = useAuth();
  const [cambiando, setCambiando] = useState<string | null>(null);
  const otros = tenants.filter((t) => t.id !== tenant?.id);

  async function irA(id: string) {
    setCambiando(id);
    try {
      await cambiarNegocio(id);
    } catch (e) {
      aviso.error(mensajeDeError(e));
    } finally {
      setCambiando(null);
    }
  }

  return (
    <div className="flex min-h-dvh justify-center bg-bg px-4 py-10 sm:items-center">
      <div className="w-full max-w-lg">
        <LogoConNombre tamano={30} className="mb-8 justify-center" />

        <Tarjeta className="p-6 sm:p-8">
          <div className="flex size-12 items-center justify-center rounded-full bg-surface-2 text-ink-soft">
            <CalendarX2 size={22} aria-hidden="true" />
          </div>
          <h1 className="mt-5 text-xl font-semibold text-ink">Suscripción vencida</h1>
          <p className="mt-2 text-sm leading-relaxed text-ink-soft">
            La suscripción de <strong className="text-ink">{tenant?.name ?? "este negocio"}</strong>{" "}
            {estado.vencio_el ? `venció el ${fechaLarga(estado.vencio_el)}` : "está vencida"} y el período
            de gracia terminó. El acceso a las ventas, el inventario y las demás funciones permanecerá
            suspendido hasta que se confirme el pago.
          </p>
          <p className="mt-2 text-sm leading-relaxed text-muted">
            La información del negocio se conserva: al renovarse la suscripción, todo vuelve a estar
            disponible tal como estaba.
          </p>

          {estado.puede_renovar ? (
            <div className="mt-6 space-y-3">
              {estado.contacto_url && (
                <>
                  <a
                    href={estado.contacto_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pulsable flex w-full items-center justify-center gap-2 rounded-[var(--radius-control)] bg-accent p-3 text-sm font-medium text-on-accent hover:bg-accent-hover"
                  >
                    <MessageCircle size={16} aria-hidden="true" />
                    {estado.contacto_texto ?? "Contactar a un asesor"}
                  </a>
                  <p className="text-center text-xs text-muted">
                    Un asesor te indicará los datos de pago. Si ya pagaste, repórtalo a continuación.
                  </p>
                </>
              )}
              <AvisarPago />
            </div>
          ) : (
            <Nota tono="neutro" className="mt-6">
              Para reactivar el acceso, comunícate con el administrador del negocio.
            </Nota>
          )}
        </Tarjeta>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm">
          {otros.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => void irA(t.id)}
              disabled={cambiando !== null}
              className="text-accent hover:underline disabled:opacity-50"
            >
              {cambiando === t.id ? "Cambiando…" : `Ir a ${t.name}`}
            </button>
          ))}
          <button
            type="button"
            onClick={() => void cerrarSesion()}
            className="inline-flex items-center gap-1.5 text-muted hover:text-ink"
          >
            <LogOut size={14} aria-hidden="true" /> Cerrar sesión
          </button>
        </div>
      </div>
    </div>
  );
}
